/**
 * PointCloudMathUtils - Mathematical utilities for point cloud operations
 *
 * Provides optimized methods for spatial queries, raycasting, and geometric operations
 * on point cloud data using octree spatial structures.
 */

import * as THREE from 'three';

/**
 * Minimal interface for point cloud octree node.
 * Designed to be structurally compatible with PointCloudOctreeNode.
 */
export interface IPointCloudNode {
  geometryNode?: {
    geometry?: THREE.BufferGeometry | null;
  };
  sceneNode?: THREE.Object3D | null;
}

/**
 * Minimal interface for point cloud with octree access.
 * Designed to be structurally compatible with PointCloudOctree.
 */
export interface IPointCloud {
  visibleNodes: IPointCloudNode[];
  nodesOnRay(nodes: IPointCloudNode[], ray: THREE.Ray): IPointCloudNode[];
}

/** Minimal interface for a clip volume used in raycasting */
export interface ClipVolumeInfo {
  clip: boolean;
  actualClipTask: number;
  matrixWorld: THREE.Matrix4;
  updateMatrixWorld(force: boolean): void;
}

/** Minimal interface for a clip plane used in raycasting */
export interface ClipPlaneInfo {
  actualClipTask: number;
  matrixWorld: THREE.Matrix4;
  tolerance?: number;
}

export interface RaycastOptions {
  /** Maximum distance along the ray to search for points */
  maxDistance?: number;
  /** Radius around the ray to consider points (cylindrical query) */
  radius?: number;
  /** Only return the first group of points found along the ray */
  firstGroupOnly?: boolean;
  /** Clip volumes to respect when picking points */
  clipVolumes?: ClipVolumeInfo[];
  /** Clip planes to respect when picking points */
  clipPlanes?: ClipPlaneInfo[];
}

/**
 * Result of raycasting with position and segment data
 */
export interface PointWithSegment {
  /** World space position of the point */
  position: THREE.Vector3;
  /** Segment IDs for this point */
  segment: number[];
  /** Distance from the point to the ray */
  distanceToRay: number;
}

/**
 * Query points along a ray directly from octree nodes
 * Optimized to use octree spatial structure with cylindrical bounding volume
 *
 * This method efficiently queries points within a cylinder around a ray by:
 * 1. Creating a bounding box for the cylinder
 * 2. Using octree spatial queries to filter nodes
 * 3. Performing precise per-point distance checks
 * 4. Filtering out points that are clipped by active clip volumes and planes
 *
 * @param pointcloud - The point cloud to query
 * @param ray - The ray to cast
 * @param maxDistance - Maximum distance along the ray (default: 1000)
 * @param radius - Cylinder radius around the ray (default: 0.5)
 * @param firstGroupOnly - Only return the first group of points found (default: false)
 * @param clipVolumes - Optional array of clip volumes to respect
 * @param clipPlanes - Optional array of clip planes to respect
 * @returns Array of points with segment data that intersect the cylindrical volume around the ray
 */
export function getPointsAlongRay(pointcloud: IPointCloud, ray: THREE.Ray, maxDistance = 1000, radius = 0.5, firstGroupOnly = false, clipVolumes?: ClipVolumeInfo[], clipPlanes?: ClipPlaneInfo[]): PointWithSegment[] {
  const points: PointWithSegment[] = [];

  // Create a cylinder bounding box for spatial queries
  // This works for rays in any direction
  const rayEnd = ray.origin.clone().addScaledVector(ray.direction, maxDistance);
  const cylinderBox = new THREE.Box3();
  cylinderBox.setFromPoints([
    new THREE.Vector3(Math.min(ray.origin.x, rayEnd.x) - radius, Math.min(ray.origin.y, rayEnd.y) - radius, Math.min(ray.origin.z, rayEnd.z) - radius),
    new THREE.Vector3(Math.max(ray.origin.x, rayEnd.x) + radius, Math.max(ray.origin.y, rayEnd.y) + radius, Math.max(ray.origin.z, rayEnd.z) + radius),
  ]);

  // Get all visible nodes that intersect with the cylinder bounding box
  const nodes = pointcloud.nodesOnRay(pointcloud.visibleNodes, ray);

  if (!nodes || nodes.length === 0) {
    return points;
  }

  // Pre-compute ray properties for faster checks
  const rayOriginX = ray.origin.x;
  const rayOriginY = ray.origin.y;
  const rayOriginZ = ray.origin.z;
  const rayDirX = ray.direction.x;
  const rayDirY = ray.direction.y;
  const rayDirZ = ray.direction.z;
  const radiusSquared = radius * radius;

  // Track points with their projection length for firstGroupOnly mode
  const pointsWithDistance: Array<{ point: PointWithSegment; projectionLength: number }> = [];

  // Pre-compute clip plane inverse matrices outside the point loop for performance
  const clipPlaneInverseMatrices: THREE.Matrix4[] = [];
  const clipPlaneTolerances: number[] = [];
  const clipPlaneClipTasks: number[] = [];

  if (clipPlanes) {
    for (const plane of clipPlanes) {
      if (plane.actualClipTask === 2 || plane.actualClipTask === 3) {
        const planeInverseMatrix = new THREE.Matrix4().copy(plane.matrixWorld).invert();
        clipPlaneInverseMatrices.push(planeInverseMatrix);
        clipPlaneTolerances.push(plane.tolerance || 0.01);
        clipPlaneClipTasks.push(plane.actualClipTask);
      }
    }
  }

  // Pre-collect active clip volumes and compute their inverse matrices once
  const activeClipVolumes: Array<{
    clipTask: number;
    inverseMatrix: THREE.Matrix4;
  }> = [];

  if (clipVolumes) {
    for (const volume of clipVolumes) {
      if (volume.clip && (volume.actualClipTask === 2 || volume.actualClipTask === 3)) {
        // Ensure world matrix is up to date
        volume.updateMatrixWorld(true);

        // Pre-compute inverse matrix for transforming points to box local space
        const inverseMatrix = new THREE.Matrix4().copy(volume.matrixWorld).invert();

        activeClipVolumes.push({
          clipTask: volume.actualClipTask,
          inverseMatrix,
        });
      }
    }
  }

  const hasActiveClips = activeClipVolumes.length > 0 || clipPlaneInverseMatrices.length > 0;

  // For each node, check points more efficiently
  for (const node of nodes) {
    if (!node.geometryNode || !node.geometryNode.geometry) {
      continue;
    }

    const geometry = node.geometryNode.geometry;
    const positions = geometry.attributes.position;

    if (!positions || !positions.array) {
      continue;
    }

    // Get segment attribute if available
    const segments = geometry.attributes.segment;
    const segmentArray = segments?.array;

    // Get world matrix once
    const worldMatrix = node.sceneNode ? node.sceneNode.matrixWorld : new THREE.Matrix4();
    const matrixElements = worldMatrix.elements;

    // Direct array access for performance
    const posArray = positions.array;
    const count = positions.count;

    for (let i = 0; i < count; i++) {
      const idx = i * 3;
      const localX = posArray[idx];
      const localY = posArray[idx + 1];
      const localZ = posArray[idx + 2];

      // Transform to world space (manual matrix multiplication)
      const worldX = matrixElements[0] * localX + matrixElements[4] * localY + matrixElements[8] * localZ + matrixElements[12];
      const worldY = matrixElements[1] * localX + matrixElements[5] * localY + matrixElements[9] * localZ + matrixElements[13];
      const worldZ = matrixElements[2] * localX + matrixElements[6] * localY + matrixElements[10] * localZ + matrixElements[14];

      // Check if point is clipped by clip volumes or planes
      // SHOW_INSIDE (2): Include points inside (union - visible if inside ANY)
      // SHOW_OUTSIDE (3): Exclude points inside (subtraction - hidden if inside ANY)
      if (hasActiveClips) {
        let isVisibleInAnyShowInside = false;
        let isHiddenByShowOutside = false;

        // First pass: Check SHOW_INSIDE clips (inclusion)
        for (let clipIdx = 0; clipIdx < activeClipVolumes.length; clipIdx++) {
          const clip = activeClipVolumes[clipIdx];
          if (clip.clipTask !== 2) continue; // Only SHOW_INSIDE

          // Transform point to box local space
          const m = clip.inverseMatrix.elements;
          const localX = m[0] * worldX + m[4] * worldY + m[8] * worldZ + m[12];
          const localY = m[1] * worldX + m[5] * worldY + m[9] * worldZ + m[13];
          const localZ = m[2] * worldX + m[6] * worldY + m[10] * worldZ + m[14];

          // Check if inside unit box [-0.5, 0.5]
          const isInside = localX >= -0.5 && localX <= 0.5 && localY >= -0.5 && localY <= 0.5 && localZ >= -0.5 && localZ <= 0.5;

          if (isInside) {
            isVisibleInAnyShowInside = true;
            break;
          }
        }

        // Check planes with SHOW_INSIDE
        if (!isVisibleInAnyShowInside) {
          for (let clipIdx = 0; clipIdx < clipPlaneInverseMatrices.length; clipIdx++) {
            if (clipPlaneClipTasks[clipIdx] !== 2) continue; // Only SHOW_INSIDE

            const m = clipPlaneInverseMatrices[clipIdx].elements;
            const localZ = m[2] * worldX + m[6] * worldY + m[10] * worldZ + m[14];
            const isInside = localZ >= -clipPlaneTolerances[clipIdx] && localZ <= clipPlaneTolerances[clipIdx];

            if (isInside) {
              isVisibleInAnyShowInside = true;
              break;
            }
          }
        }

        // If there are SHOW_INSIDE clips but point is not inside any, skip it
        const hasShowInside = activeClipVolumes.some((v) => v.clipTask === 2) || clipPlaneClipTasks.some((t) => t === 2);
        if (hasShowInside && !isVisibleInAnyShowInside) {
          continue;
        }

        // Second pass: Check SHOW_OUTSIDE clips (exclusion/holes)
        for (let clipIdx = 0; clipIdx < activeClipVolumes.length; clipIdx++) {
          const clip = activeClipVolumes[clipIdx];
          if (clip.clipTask !== 3) continue; // Only SHOW_OUTSIDE

          // Transform point to box local space
          const m = clip.inverseMatrix.elements;
          const localX = m[0] * worldX + m[4] * worldY + m[8] * worldZ + m[12];
          const localY = m[1] * worldX + m[5] * worldY + m[9] * worldZ + m[13];
          const localZ = m[2] * worldX + m[6] * worldY + m[10] * worldZ + m[14];

          // Check if inside unit box [-0.5, 0.5]
          const isInside = localX >= -0.5 && localX <= 0.5 && localY >= -0.5 && localY <= 0.5 && localZ >= -0.5 && localZ <= 0.5;

          if (isInside) {
            isHiddenByShowOutside = true;
            break;
          }
        }

        // Check planes with SHOW_OUTSIDE
        if (!isHiddenByShowOutside) {
          for (let clipIdx = 0; clipIdx < clipPlaneInverseMatrices.length; clipIdx++) {
            if (clipPlaneClipTasks[clipIdx] !== 3) continue; // Only SHOW_OUTSIDE

            const m = clipPlaneInverseMatrices[clipIdx].elements;
            const localZ = m[2] * worldX + m[6] * worldY + m[10] * worldZ + m[14];
            const isInside = localZ >= -clipPlaneTolerances[clipIdx] && localZ <= clipPlaneTolerances[clipIdx];

            if (isInside) {
              isHiddenByShowOutside = true;
              break;
            }
          }
        }

        // Skip point if it's hidden by any SHOW_OUTSIDE clip
        if (isHiddenByShowOutside) {
          continue;
        }
      }

      // Quick bounding box check first
      if (worldX < cylinderBox.min.x || worldX > cylinderBox.max.x || worldY < cylinderBox.min.y || worldY > cylinderBox.max.y || worldZ < cylinderBox.min.z || worldZ > cylinderBox.max.z) {
        continue;
      }

      // Check if point is along the ray direction (not behind ray origin)
      const rayToPointX = worldX - rayOriginX;
      const rayToPointY = worldY - rayOriginY;
      const rayToPointZ = worldZ - rayOriginZ;
      const projectionLength = rayToPointX * rayDirX + rayToPointY * rayDirY + rayToPointZ * rayDirZ;

      if (projectionLength < 0 || projectionLength > maxDistance) {
        continue;
      }

      // Calculate closest point on ray (without creating objects)
      const closestX = rayOriginX + rayDirX * projectionLength;
      const closestY = rayOriginY + rayDirY * projectionLength;
      const closestZ = rayOriginZ + rayDirZ * projectionLength;

      // Distance squared check (avoid sqrt)
      const dx = worldX - closestX;
      const dy = worldY - closestY;
      const dz = worldZ - closestZ;
      const distanceSquared = dx * dx + dy * dy + dz * dz;

      if (distanceSquared <= radiusSquared) {
        // Extract segment data for this point
        const segmentIds: number[] = [];
        if (segmentArray) {
          // Segment attribute stores segment IDs - could be multiple per point
          const segmentValue = segmentArray[i];
          if (segmentValue !== undefined && segmentValue !== null) {
            segmentIds.push(segmentValue);
          }
        }

        const distanceToRay = Math.sqrt(distanceSquared);
        const pointWithSegment: PointWithSegment = {
          position: new THREE.Vector3(worldX, worldY, worldZ),
          segment: segmentIds,
          distanceToRay,
        };

        if (firstGroupOnly) {
          pointsWithDistance.push({ point: pointWithSegment, projectionLength });
        } else {
          points.push(pointWithSegment);
        }
      }
    }
  }

  // If firstGroupOnly is enabled, find the closest group
  if (firstGroupOnly && pointsWithDistance.length > 0) {
    // Sort by projection length to find the closest point
    pointsWithDistance.sort((a, b) => a.projectionLength - b.projectionLength);

    // Find the 5 closest points to the first (closest to camera) point
    const firstPoint = pointsWithDistance[0].point.position;
    const closestPoints: { index: number; distance: number }[] = [];

    for (let i = 1; i < pointsWithDistance.length; i++) {
      const distance = firstPoint.distanceTo(pointsWithDistance[i].point.position);
      closestPoints.push({ index: i, distance });
    }

    // Filter out zero distances and apply 5cm hard cap, then sort by distance and take the 5 closest
    const nonZeroPoints = closestPoints.filter((p) => p.distance > 0.005);
    nonZeroPoints.sort((a, b) => a.distance - b.distance);
    const top5 = nonZeroPoints.slice(0, Math.min(10, nonZeroPoints.length));

    // Find the average distance among these 6 points (first + 5 closest)
    let totalDistance = 0;
    let distanceCount = 0;
    const top5Positions = [firstPoint, ...top5.map((p) => pointsWithDistance[p.index].point.position)];

    for (let i = 0; i < top5Positions.length; i++) {
      for (let j = i + 1; j < top5Positions.length; j++) {
        const dist = top5Positions[i].distanceTo(top5Positions[j]);
        totalDistance += dist;
        distanceCount++;
      }
    }

    const avgDistanceInGroup = distanceCount > 0 ? totalDistance / distanceCount : radius;

    // Use this average distance as the threshold (with 50% margin), capped at 5cm
    const threshold = avgDistanceInGroup * 1.5;

    // Start with the first point and the 5 closest points as the initial cluster
    const clusterPoints: THREE.Vector3[] = top5Positions;
    points.push(pointsWithDistance[0].point);
    const used = new Set<number>([0]);

    // Add the top 5 closest points to the result
    for (const p of top5) {
      points.push(pointsWithDistance[p.index].point);
      used.add(p.index);
    }

    // Keep checking remaining points until no more can be added
    let addedInLastPass = true;

    while (addedInLastPass) {
      addedInLastPass = false;

      for (let i = 1; i < pointsWithDistance.length; i++) {
        if (used.has(i)) continue;

        const item = pointsWithDistance[i];

        // Check if this point is close enough to ANY point in the cluster (using 3D distance)
        let isCloseToCluster = false;
        for (const clusterPoint of clusterPoints) {
          const distance = item.point.position.distanceTo(clusterPoint);
          if (distance <= threshold) {
            isCloseToCluster = true;
            break;
          }
        }

        if (isCloseToCluster) {
          points.push(item.point);
          clusterPoints.push(item.point.position);
          used.add(i);
          addedInLastPass = true;
        }
      }
    }
  }

  return points;
}

export interface PlaneDetectionOptions {
  /** Maximum distance along the ray to search (default: 1000) */
  maxDistance?: number;
  /** Radius around the ray to consider points (default: 0.5) */
  radius?: number;
  /** Thickness tolerance for considering points as coplanar (default: 0.05) */
  planeThickness?: number;
  /** Minimum number of points required to confirm a plane (default: 10) */
  minPointsForPlane?: number;
  /** Step size along the ray to check for planes (default: 0.1) */
  stepSize?: number;
  /** Minimum coverage ratio of the radius circle (0-1, default: 0.5) */
  minCoverageRatio?: number;
  /** Clip volumes to respect when picking points */
  clipVolumes?: ClipVolumeInfo[];
  /** Clip planes to respect when picking points */
  clipPlanes?: ClipPlaneInfo[];
  /** Whether to collect debug information about traversed/skipped points (default: false) */
  collectDebugInfo?: boolean;
  /** If true, return the closest point as a fallback when no valid plane is found (default: false) */
  fallbackToFirstPoint?: boolean;
}

export interface PlaneDetectionDebugInfo {
  /** All points that were traversed during detection */
  allTraversedPoints: PointWithSegment[];
  /** Points that were skipped/rejected */
  skippedPoints: PointWithSegment[];
  /** Reasons why each skipped point was rejected */
  skipReasons: ('behind_ray' | 'beyond_max_distance' | 'outside_radius' | 'not_in_plane' | 'low_coverage')[];
}

export interface PlaneDetectionResult {
  /** Position of the detected plane (center point on the ray) */
  position: THREE.Vector3;
  /** Distance from ray origin to the plane */
  distance: number;
  /** Points that belong to the detected plane */
  points: PointWithSegment[];
  /** Normal of the plane (same as ray direction, pointing back toward origin) */
  normal: THREE.Vector3;
  /** Debug information about traversed and skipped points (only populated when collectDebugInfo is true) */
  debugInfo?: PlaneDetectionDebugInfo;
}

/**
 * Find the first planar surface perpendicular to the ray direction.
 *
 * This function searches along a ray for clusters of points that lie on a plane
 * perpendicular to the ray. It's useful for detecting walls, floors, doors, or
 * other flat surfaces when looking straight at them.
 *
 * OPTIMIZED: Processes nodes sorted by distance and exits early once a valid plane is found.
 *
 * Algorithm:
 * 1. Sort nodes by distance to ray origin
 * 2. Process nodes incrementally, collecting points
 * 3. After each node, check if we have a valid plane (early exit)
 * 4. Use sliding window to find cluster of points within planeThickness
 * 5. Verify points are distributed across the radius
 *
 * @param pointcloud - The point cloud to query
 * @param ray - The ray to cast (plane will be perpendicular to ray.direction)
 * @param options - Detection options
 * @returns PlaneDetectionResult if a plane is found, null otherwise
 */
export function getFirstPlaneAlongRay(pointcloud: IPointCloud, ray: THREE.Ray, options: PlaneDetectionOptions = {}): PlaneDetectionResult | null {
  const { maxDistance = 1000, radius = 0.5, planeThickness = 0.05, minPointsForPlane = 10, minCoverageRatio = 0.75, collectDebugInfo = false } = options;

  // Debug info collection
  const debugAllTraversed: PointWithSegment[] = [];
  const debugSkipped: PointWithSegment[] = [];
  const debugSkipReasons: ('behind_ray' | 'beyond_max_distance' | 'outside_radius' | 'not_in_plane' | 'low_coverage')[] = [];

  // Get nodes that intersect with the ray
  const nodes = pointcloud.nodesOnRay(pointcloud.visibleNodes, ray);
  if (!nodes || nodes.length === 0) {
    return null;
  }

  // Find the maximum octree level (most detailed) among all intersecting nodes
  // We only want to sample from nodes at this level to ensure consistent point density
  // Mixing levels would give us sparse points from low-detail nodes alongside dense points
  let maxOctreeLevel = 0;
  for (const node of nodes) {
    const geomNode = node.geometryNode as { level?: number; getLevel?: () => number } | undefined;
    const level = geomNode?.getLevel?.() ?? geomNode?.level ?? 0;
    maxOctreeLevel = Math.max(maxOctreeLevel, level);
  }

  // Filter and process nodes - only use nodes at the maximum level
  const nodesWithDistance = nodes
    .filter((node) => {
      const geomNode = node.geometryNode as { level?: number; getLevel?: () => number } | undefined;
      const level = geomNode?.getLevel?.() ?? geomNode?.level ?? 0;
      return level === maxOctreeLevel;
    })
    .map((node) => {
      let minProjection = Infinity;
      let maxProjection = -Infinity;

      // Get bounding box from geometryNode (which is PointCloudOctreeGeometryNode)
      const geomNode = node.geometryNode as { boundingBox?: THREE.Box3 } | undefined;
      const bbox = geomNode?.boundingBox;

      if (bbox && node.sceneNode) {
        // Transform bounding box to world space using scene node's matrix
        const worldBbox = bbox.clone().applyMatrix4(node.sceneNode.matrixWorld);

        // Check all 8 corners of the bounding box to find min/max projection along ray
        const corners = [
          new THREE.Vector3(worldBbox.min.x, worldBbox.min.y, worldBbox.min.z),
          new THREE.Vector3(worldBbox.min.x, worldBbox.min.y, worldBbox.max.z),
          new THREE.Vector3(worldBbox.min.x, worldBbox.max.y, worldBbox.min.z),
          new THREE.Vector3(worldBbox.min.x, worldBbox.max.y, worldBbox.max.z),
          new THREE.Vector3(worldBbox.max.x, worldBbox.min.y, worldBbox.min.z),
          new THREE.Vector3(worldBbox.max.x, worldBbox.min.y, worldBbox.max.z),
          new THREE.Vector3(worldBbox.max.x, worldBbox.max.y, worldBbox.min.z),
          new THREE.Vector3(worldBbox.max.x, worldBbox.max.y, worldBbox.max.z),
        ];

        for (const corner of corners) {
          const toCorner = corner.clone().sub(ray.origin);
          const projection = toCorner.dot(ray.direction);
          minProjection = Math.min(minProjection, projection);
          maxProjection = Math.max(maxProjection, projection);
        }
      }
      return { node, minProjection, maxProjection };
    });

  // Sort by minimum projection distance - process closer nodes first
  nodesWithDistance.sort((a, b) => a.minProjection - b.minProjection);

  // Create orthonormal basis vectors perpendicular to ray direction
  const basisU = new THREE.Vector3();
  const basisV = new THREE.Vector3();
  createOrthonormalBasis(ray.direction, basisU, basisV);

  // Pre-compute ray properties
  const rayOriginX = ray.origin.x;
  const rayOriginY = ray.origin.y;
  const rayOriginZ = ray.origin.z;
  const rayDirX = ray.direction.x;
  const rayDirY = ray.direction.y;
  const rayDirZ = ray.direction.z;
  const radiusSquared = radius * radius;

  // Collected points with projection data
  const collectedPoints: Array<{
    point: PointWithSegment;
    projectionDistance: number;
    planeU: number;
    planeV: number;
  }> = [];

  // Track if we found a valid plane and at what distance
  let foundPlaneDistance: number | null = null;

  // Process nodes in distance order
  for (const { node, minProjection } of nodesWithDistance) {
    // Early exit: only if we already found a valid plane AND this node's closest possible
    // point is beyond the plane (no point in this node can be part of a closer plane)
    if (foundPlaneDistance !== null && minProjection > foundPlaneDistance + planeThickness) {
      break;
    }

    if (!node.geometryNode || !node.geometryNode.geometry) {
      continue;
    }

    const geometry = node.geometryNode.geometry;
    const positions = geometry.attributes.position;

    if (!positions || !positions.array) {
      continue;
    }

    // Get segment attribute if available
    const segments = geometry.attributes.segment;
    const segmentArray = segments?.array;

    // Get world matrix
    const worldMatrix = node.sceneNode ? node.sceneNode.matrixWorld : new THREE.Matrix4();
    const matrixElements = worldMatrix.elements;

    const posArray = positions.array;
    const count = positions.count;

    // Process points in this node
    for (let i = 0; i < count; i++) {
      const idx = i * 3;
      const localX = posArray[idx];
      const localY = posArray[idx + 1];
      const localZ = posArray[idx + 2];

      // Transform to world space
      const worldX = matrixElements[0] * localX + matrixElements[4] * localY + matrixElements[8] * localZ + matrixElements[12];
      const worldY = matrixElements[1] * localX + matrixElements[5] * localY + matrixElements[9] * localZ + matrixElements[13];
      const worldZ = matrixElements[2] * localX + matrixElements[6] * localY + matrixElements[10] * localZ + matrixElements[14];

      // Calculate projection along ray
      const rayToPointX = worldX - rayOriginX;
      const rayToPointY = worldY - rayOriginY;
      const rayToPointZ = worldZ - rayOriginZ;
      const projectionLength = rayToPointX * rayDirX + rayToPointY * rayDirY + rayToPointZ * rayDirZ;

      // Skip points behind ray or beyond maxDistance
      if (projectionLength < 0) {
        if (collectDebugInfo) {
          const skipPoint: PointWithSegment = {
            position: new THREE.Vector3(worldX, worldY, worldZ),
            segment: [],
            distanceToRay: 0,
          };
          debugAllTraversed.push(skipPoint);
          debugSkipped.push(skipPoint);
          debugSkipReasons.push('behind_ray');
        }
        continue;
      }
      if (projectionLength > maxDistance) {
        if (collectDebugInfo) {
          const skipPoint: PointWithSegment = {
            position: new THREE.Vector3(worldX, worldY, worldZ),
            segment: [],
            distanceToRay: 0,
          };
          debugAllTraversed.push(skipPoint);
          debugSkipped.push(skipPoint);
          debugSkipReasons.push('beyond_max_distance');
        }
        continue;
      }

      // Calculate distance from ray
      const closestX = rayOriginX + rayDirX * projectionLength;
      const closestY = rayOriginY + rayDirY * projectionLength;
      const closestZ = rayOriginZ + rayDirZ * projectionLength;

      const dx = worldX - closestX;
      const dy = worldY - closestY;
      const dz = worldZ - closestZ;
      const distanceSquared = dx * dx + dy * dy + dz * dz;

      if (distanceSquared > radiusSquared) {
        if (collectDebugInfo) {
          const skipPoint: PointWithSegment = {
            position: new THREE.Vector3(worldX, worldY, worldZ),
            segment: [],
            distanceToRay: Math.sqrt(distanceSquared),
          };
          debugAllTraversed.push(skipPoint);
          debugSkipped.push(skipPoint);
          debugSkipReasons.push('outside_radius');
        }
        continue;
      }

      // Calculate 2D coordinates in plane perpendicular to ray
      const u = rayToPointX * basisU.x + rayToPointY * basisU.y + rayToPointZ * basisU.z;
      const v = rayToPointX * basisV.x + rayToPointY * basisV.y + rayToPointZ * basisV.z;

      // Extract segment data
      const segment: number[] = [];
      if (segmentArray) {
        const segValue = segmentArray[i];
        if (segValue !== undefined && segValue !== 0) {
          segment.push(segValue);
        }
      }

      // Distance from point to ray (already calculated as sqrt of distanceSquared)
      const distanceToRay = Math.sqrt(distanceSquared);

      const pointWithSegment: PointWithSegment = {
        position: new THREE.Vector3(worldX, worldY, worldZ),
        segment,
        distanceToRay,
      };

      collectedPoints.push({
        point: pointWithSegment,
        projectionDistance: projectionLength,
        planeU: u,
        planeV: v,
      });

      // Track all traversed points for debug
      if (collectDebugInfo) {
        debugAllTraversed.push(pointWithSegment);
      }
    }

    // After processing each node, check if we can find a valid plane
    if (collectedPoints.length >= minPointsForPlane) {
      // Sort by projection distance
      collectedPoints.sort((a, b) => a.projectionDistance - b.projectionDistance);

      // Try to find a valid plane using sliding window
      const { result } = tryFindPlane(
        collectedPoints,
        planeThickness,
        minPointsForPlane,
        minCoverageRatio,
        radius,
        ray,
        false, // Don't collect debug info yet, we may find a closer plane later
      );

      if (result) {
        // Found a valid plane - record its distance for early exit check
        // But DON'T return yet - there may be nodes with minProjection < result.distance
        // that could contribute to an even closer plane
        if (foundPlaneDistance === null || result.distance < foundPlaneDistance) {
          foundPlaneDistance = result.distance;
        }
      }
    }
  }

  // Final plane detection with all collected points
  // This is where we actually return the result after processing all relevant nodes
  if (collectedPoints.length >= minPointsForPlane) {
    collectedPoints.sort((a, b) => a.projectionDistance - b.projectionDistance);
    const { result, notInPlanePoints } = tryFindPlane(collectedPoints, planeThickness, minPointsForPlane, minCoverageRatio, radius, ray, collectDebugInfo);

    if (result && collectDebugInfo) {
      // Add not-in-plane points to skipped list
      if (notInPlanePoints) {
        for (const p of notInPlanePoints) {
          debugSkipped.push(p);
          debugSkipReasons.push('not_in_plane');
        }
      }

      result.debugInfo = {
        allTraversedPoints: debugAllTraversed,
        skippedPoints: debugSkipped,
        skipReasons: debugSkipReasons,
      };
    }

    return result;
  }

  return null;
}

/** Internal result type for tryFindPlane that includes skipped points info */
interface TryFindPlaneResult {
  result: PlaneDetectionResult | null;
  /** Points that were not part of the plane (only when collectDebugInfo is true) */
  notInPlanePoints?: PointWithSegment[];
}

/**
 * Helper function to find a valid plane from collected points using sliding window.
 */
function tryFindPlane(
  pointsWithProjection: Array<{
    point: PointWithSegment;
    projectionDistance: number;
    planeU: number;
    planeV: number;
  }>,
  planeThickness: number,
  minPointsForPlane: number,
  minCoverageRatio: number,
  radius: number,
  ray: THREE.Ray,
  collectDebugInfo = false,
): TryFindPlaneResult {
  let windowStart = 0;
  const notInPlanePoints: PointWithSegment[] = [];

  while (windowStart < pointsWithProjection.length) {
    const startDistance = pointsWithProjection[windowStart].projectionDistance;

    // Skip negative projections
    if (startDistance < 0) {
      windowStart++;
      continue;
    }

    // Find all points within planeThickness
    const planePointsData: Array<{ point: PointWithSegment; planeU: number; planeV: number }> = [];
    let windowEnd = windowStart;
    let totalProjection = 0;

    while (windowEnd < pointsWithProjection.length) {
      const currentDistance = pointsWithProjection[windowEnd].projectionDistance;

      if (currentDistance - startDistance <= planeThickness) {
        planePointsData.push({
          point: pointsWithProjection[windowEnd].point,
          planeU: pointsWithProjection[windowEnd].planeU,
          planeV: pointsWithProjection[windowEnd].planeV,
        });
        totalProjection += currentDistance;
        windowEnd++;
      } else {
        break;
      }
    }

    // Check if we found enough points for a valid plane
    if (planePointsData.length >= minPointsForPlane) {
      const coverage = calculatePlaneCoverage(planePointsData, radius);

      if (coverage >= minCoverageRatio) {
        const avgProjection = totalProjection / planePointsData.length;
        const planePosition = ray.origin.clone().addScaledVector(ray.direction, avgProjection);
        const normal = ray.direction.clone().negate();

        // Collect points not in the plane for debug
        if (collectDebugInfo) {
          const planePointSet = new Set(planePointsData.map((p) => p.point));
          for (const p of pointsWithProjection) {
            if (!planePointSet.has(p.point)) {
              notInPlanePoints.push(p.point);
            }
          }
        }

        return {
          result: {
            position: planePosition,
            distance: avgProjection,
            points: planePointsData.map((p) => p.point),
            normal,
          },
          notInPlanePoints: collectDebugInfo ? notInPlanePoints : undefined,
        };
      }
    }

    windowStart++;
  }

  // No plane found - all points are "not in plane"
  if (collectDebugInfo) {
    for (const p of pointsWithProjection) {
      notInPlanePoints.push(p.point);
    }
  }

  return {
    result: null,
    notInPlanePoints: collectDebugInfo ? notInPlanePoints : undefined,
  };
}

/**
 * Create an orthonormal basis from a direction vector.
 * Returns two vectors (u, v) that are perpendicular to the direction and to each other.
 */
function createOrthonormalBasis(direction: THREE.Vector3, outU: THREE.Vector3, outV: THREE.Vector3): void {
  // Find a vector not parallel to direction
  const temp = Math.abs(direction.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);

  // Create orthonormal basis using cross products
  outU.crossVectors(direction, temp).normalize();
  outV.crossVectors(direction, outU).normalize();
}

/**
 * Calculate how well the points cover the radius circle.
 * Returns a ratio (0-1) where 1 means full coverage.
 *
 * Uses a simple approach: divide the circle into quadrants and check
 * if points exist in multiple quadrants and span a significant portion of the radius.
 */
function calculatePlaneCoverage(points: Array<{ planeU: number; planeV: number }>, radius: number): number {
  if (points.length === 0) return 0;

  // Calculate bounding box of points in the plane
  let minU = Infinity,
    maxU = -Infinity;
  let minV = Infinity,
    maxV = -Infinity;

  // Count points in each quadrant
  let q1 = 0,
    q2 = 0,
    q3 = 0,
    q4 = 0;

  for (const p of points) {
    minU = Math.min(minU, p.planeU);
    maxU = Math.max(maxU, p.planeU);
    minV = Math.min(minV, p.planeV);
    maxV = Math.max(maxV, p.planeV);

    // Count quadrants (based on sign of U and V)
    if (p.planeU >= 0 && p.planeV >= 0) q1++;
    else if (p.planeU < 0 && p.planeV >= 0) q2++;
    else if (p.planeU < 0 && p.planeV < 0) q3++;
    else q4++;
  }

  // Calculate spread as ratio of bounding box to circle diameter
  const spreadU = (maxU - minU) / (2 * radius);
  const spreadV = (maxV - minV) / (2 * radius);

  // Calculate quadrant coverage (how many quadrants have points)
  const occupiedQuadrants = (q1 > 0 ? 1 : 0) + (q2 > 0 ? 1 : 0) + (q3 > 0 ? 1 : 0) + (q4 > 0 ? 1 : 0);
  const quadrantCoverage = occupiedQuadrants / 4;

  // Combined coverage: average of spread and quadrant coverage
  // A wall facing the ray will have high spread and multiple quadrants
  // An angled wall will have low spread (clustered in one area)
  const coverage = (spreadU + spreadV + quadrantCoverage) / 3;

  return Math.min(1, coverage);
}

/**
 * Find the first planar surface perpendicular to the ray direction.
 * This is the ACCURATE (unoptimized) version that uses ALL points from all octree levels.
 *
 * Use this for doors and windows where accuracy is more important than performance.
 * For room detection where speed matters more, use getFirstPlaneAlongRay instead.
 *
 * Algorithm:
 * 1. Collect ALL points along the ray using getPointsAlongRay
 * 2. Sort by projection distance
 * 3. Use sliding window to find cluster of points within planeThickness
 * 4. Verify points are distributed across the radius
 *
 * @param pointcloud - The point cloud to query
 * @param ray - The ray to cast (plane will be perpendicular to ray.direction)
 * @param options - Detection options
 * @returns PlaneDetectionResult if a plane is found, null otherwise
 */
export function getFirstPlaneAlongRayAccurate(pointcloud: IPointCloud, ray: THREE.Ray, options: PlaneDetectionOptions = {}): PlaneDetectionResult | null {
  const { maxDistance = 1000, radius = 0.5, planeThickness = 0.05, minPointsForPlane = 10, minCoverageRatio = 0.75, clipVolumes, clipPlanes, collectDebugInfo = false, fallbackToFirstPoint = false } = options;

  // Debug info collection
  const debugAllTraversed: PointWithSegment[] = [];
  const debugSkipped: PointWithSegment[] = [];
  const debugSkipReasons: ('behind_ray' | 'beyond_max_distance' | 'outside_radius' | 'not_in_plane' | 'low_coverage')[] = [];

  // Get ALL points along the ray (uses all octree levels, no filtering)
  const allPoints = getPointsAlongRay(pointcloud, ray, maxDistance, radius, false, clipVolumes, clipPlanes);

  if (collectDebugInfo) {
    debugAllTraversed.push(...allPoints);
  }

  // Helper to create fallback result from closest point
  const createFallbackResult = (): PlaneDetectionResult | null => {
    if (!fallbackToFirstPoint || allPoints.length === 0) {
      return null;
    }

    // Find the closest point along the ray
    let closestPoint = allPoints[0];
    let closestDistance = Infinity;

    for (const point of allPoints) {
      const toPoint = point.position.clone().sub(ray.origin);
      const projectionDistance = toPoint.dot(ray.direction);
      if (projectionDistance >= 0 && projectionDistance < closestDistance) {
        closestDistance = projectionDistance;
        closestPoint = point;
      }
    }

    if (closestDistance === Infinity) {
      return null;
    }

    const planePosition = ray.origin.clone().addScaledVector(ray.direction, closestDistance);
    const normal = ray.direction.clone().negate();

    let debugInfo: PlaneDetectionDebugInfo | undefined;
    if (collectDebugInfo) {
      debugInfo = {
        allTraversedPoints: debugAllTraversed,
        skippedPoints: debugSkipped,
        skipReasons: debugSkipReasons,
      };
    }

    return {
      position: planePosition,
      distance: closestDistance,
      points: [closestPoint],
      normal,
      debugInfo,
    };
  };

  if (allPoints.length < minPointsForPlane) {
    return createFallbackResult();
  }

  // Create orthonormal basis vectors perpendicular to ray direction
  // These are used to project points onto the plane perpendicular to the ray
  const basisU = new THREE.Vector3();
  const basisV = new THREE.Vector3();
  createOrthonormalBasis(ray.direction, basisU, basisV);

  // Calculate projection distance for each point (distance along ray direction)
  // Also calculate the 2D position in the plane perpendicular to the ray
  const pointsWithProjection = allPoints.map((point) => {
    const toPoint = point.position.clone().sub(ray.origin);
    const projectionDistance = toPoint.dot(ray.direction);

    // Project onto the plane perpendicular to ray (2D coordinates)
    const u = toPoint.dot(basisU);
    const v = toPoint.dot(basisV);

    return {
      point,
      projectionDistance,
      planeU: u,
      planeV: v,
    };
  });

  // Sort by projection distance (closest to ray origin first)
  pointsWithProjection.sort((a, b) => a.projectionDistance - b.projectionDistance);

  // Sliding window approach: find the first cluster of points within planeThickness
  let windowStart = 0;

  while (windowStart < pointsWithProjection.length) {
    const startDistance = pointsWithProjection[windowStart].projectionDistance;

    // Skip negative projections (behind ray origin)
    if (startDistance < 0) {
      windowStart++;
      continue;
    }

    // Find all points within planeThickness of the window start
    const planePointsData: Array<{ point: PointWithSegment; planeU: number; planeV: number }> = [];
    let windowEnd = windowStart;
    let totalProjection = 0;

    while (windowEnd < pointsWithProjection.length) {
      const currentDistance = pointsWithProjection[windowEnd].projectionDistance;

      if (currentDistance - startDistance <= planeThickness) {
        planePointsData.push({
          point: pointsWithProjection[windowEnd].point,
          planeU: pointsWithProjection[windowEnd].planeU,
          planeV: pointsWithProjection[windowEnd].planeV,
        });
        totalProjection += currentDistance;
        windowEnd++;
      } else {
        break;
      }
    }

    // Check if we found enough points for a valid plane
    if (planePointsData.length >= minPointsForPlane) {
      // Check coverage: points should be distributed across the radius circle
      const coverage = calculatePlaneCoverage(planePointsData, radius);

      if (coverage >= minCoverageRatio) {
        const avgProjection = totalProjection / planePointsData.length;

        // Calculate plane position along the ray
        const planePosition = ray.origin.clone().addScaledVector(ray.direction, avgProjection);

        // Normal points back toward the ray origin (opposite of ray direction)
        const normal = ray.direction.clone().negate();

        // Collect debug info for points not in the plane
        let debugInfo: PlaneDetectionDebugInfo | undefined;
        if (collectDebugInfo) {
          const planePointSet = new Set(planePointsData.map((p) => p.point));
          for (const p of pointsWithProjection) {
            if (!planePointSet.has(p.point)) {
              debugSkipped.push(p.point);
              debugSkipReasons.push('not_in_plane');
            }
          }
          debugInfo = {
            allTraversedPoints: debugAllTraversed,
            skippedPoints: debugSkipped,
            skipReasons: debugSkipReasons,
          };
        }

        return {
          position: planePosition,
          distance: avgProjection,
          points: planePointsData.map((p) => p.point),
          normal,
          debugInfo,
        };
      }
    }

    // Move window start forward
    windowStart++;
  }

  // No plane found - add all points as skipped for debug
  if (collectDebugInfo) {
    for (const p of pointsWithProjection) {
      debugSkipped.push(p.point);
      debugSkipReasons.push('not_in_plane');
    }
  }

  // Try fallback to first point if enabled
  return createFallbackResult();
}

/**
 * Query points along multiple rays in batch (useful for grid-based sampling)
 *
 * @param pointcloud - The point cloud to query
 * @param rays - Array of rays to cast
 * @param options - Raycast options (maxDistance, radius, clipVolumes, clipPlanes)
 * @returns Array of point arrays with segment data, one for each ray
 */
export function getPointsAlongRays(pointcloud: IPointCloud, rays: THREE.Ray[], options: RaycastOptions = {}): PointWithSegment[][] {
  const { maxDistance = 1000, radius = 0.5, firstGroupOnly = false, clipVolumes, clipPlanes } = options;

  return rays.map((ray) => getPointsAlongRay(pointcloud, ray, maxDistance, radius, firstGroupOnly, clipVolumes, clipPlanes));
}
